import { create } from "zustand";
import _ from "lodash";
import type { ViewMode, SortOption } from "@/types";
import { FilterSetting } from "@/types/filter";
import { DEFAULT_FILTER } from "@/constants/filter";
import { ClothingItem } from "@/types/product";
import { ProductService } from "@/services/product.service";

const SORT_ORDERING: Record<SortOption, string> = {
  newest: "-sort_date",
  oldest: "sort_date",
  "a-to-z": "name",
  "z-to-a": "-name",
};

interface ClosetState {
  items: ClothingItem[];
  activeMemberId: string;
  activeCategoryName: string | null;
  searchQuery: string;
  sortOption: SortOption;
  viewMode: ViewMode;
  loadingInitialPage: boolean;
  loadingNextPage: boolean;
  filter: FilterSetting;
}

interface ClosetActions {
  setItems: (items: ClothingItem[]) => void;
  setActiveMember: (memberId: string) => void;
  setActiveCategory: (categoryName: string | null) => void;
  toggleCategory: (categoryName: string) => void;
  setSearchQuery: (query: string) => void;
  setSortOption: (option: SortOption) => void;
  setViewMode: (mode: ViewMode) => void;
  reset: () => void;
  getClosetItems: (filter?: FilterSetting | undefined, paginate_results?: boolean) => void;
  updateClothingItemById: (itemId: string) => Promise<void>;
}

const initialState: ClosetState = {
  items: [],
  activeMemberId: "all",
  activeCategoryName: null,
  searchQuery: "",
  sortOption: "newest",
  viewMode: "grid",
  loadingInitialPage: false,
  loadingNextPage: false,
  filter: DEFAULT_FILTER
};

export const useClosetStore = create<ClosetState & ClosetActions>()((set, get) => ({
  ...initialState,

  setItems: (items) => {
    set({ items });
  },

  setActiveMember: (memberId) => {
    set({ activeMemberId: memberId, activeCategoryName: null });
  },

  setActiveCategory: (categoryName) => {
    set({ activeCategoryName: categoryName });
  },

  toggleCategory: (categoryName) => {
    set((state) => ({
      activeCategoryName: state.activeCategoryName === categoryName ? null : categoryName
    }));
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  setSortOption: (option) => {
    set({ sortOption: option });
    const { filter } = get();
    get().getClosetItems({ page: 1, page_size: filter.page_size, selectedSortBy: [SORT_ORDERING[option]] });
  },

  setViewMode: (mode) => {
    set({ viewMode: mode });
  },

  reset: () => {
    set(initialState);
  },

  getClosetItems: async (filter, paginate_results = false) => {
    let previousClosetItems = get().items;
    let previousClosetFilters = get().filter;
    const { loadingInitialPage, loadingNextPage } = get();

    if (loadingInitialPage || loadingNextPage) {
      return;
    }

    if (!paginate_results) {
      set({ loadingInitialPage: true });
    } else {
      set({ loadingNextPage: true });
    }

    // On first load of closet items, we might not have any filter defined, so set this to be the default
    if (!filter) {
      filter = _.cloneDeep(DEFAULT_FILTER);
    }

    // Always include current sort ordering
    if (!filter.selectedSortBy || filter.selectedSortBy.length === 0) {
      filter.selectedSortBy = [SORT_ORDERING[get().sortOption]];
    }

    try {
      // If we aren't paginating always get products, if we are paginating only get products if we have a valid next page
      if (!paginate_results || (paginate_results && previousClosetFilters?.next_page)) {
        let { results, count, next, previous } = await ProductService.getProducts(
          filter.page,
          filter.page_size,
          filter
        );

        // Set the items that is favorited
        const itemsFavorited = new Set<string>();
        results.forEach((r) => {
          if (r.is_favorite) {
            itemsFavorited.add(r.id);
          }
        });

        if (paginate_results) {
          // Short circuit if we are paginating and we receive a result we already have
          if (previousClosetItems.some((ci) => ci.id === results?.[0]?.id)) {
            return;
          }

          // Sometimes we want to add to the results -- like when we paginate
          previousClosetItems = [...previousClosetItems, ...results];
        } else {
          // And other times we want to reset the results -- like when we apply a new filter
          previousClosetItems = results;
        }

        // If next page is available, set it to be the current page + 1
        let nextPage = next ? Number(filter.page) + 1 : null;

        // Update the current filter
        previousClosetFilters = {
          ...previousClosetFilters,
          ...filter,
          next_page: nextPage,
          previous_page: Number(previous),
          total_items: Number(count)
        };

        set({
          items: previousClosetItems,
          filter: previousClosetFilters
        });
      }
    } catch (error) {
      console.error("Error when we tried to get clothings by filter and carousel", error);
      throw error;
    } finally {
      set({ loadingInitialPage: false, loadingNextPage: false });
    }
  },
  updateClothingItemById: async (itemId: string) => {
    let previousClosetItems = get().items;

    try {
      const result = await ProductService.getProductById(itemId);

      previousClosetItems = previousClosetItems.map((product) => {
        if (product.id == result.id) {
          return result;
        } else {
          return product;
        }
      });

      set({ items: previousClosetItems });
    } catch (error) {}
  }
}));
